import type { CrystalOrder, CrystalOrders, Http, MonetizationView, RewardedSession } from '@mezha/api'
import { http } from '../api/client'
import { vkLaunchParams } from './detect'
import { VK_APP_ID, VkBusy, VkUnavailable, vkSdk, type VkPlatform } from './vk'

type Transport = Pick<Http, 'get' | 'command'>
type SDK = Pick<VkPlatform, 'capabilities' | 'purchase' | 'rewardAvailable' | 'showReward'>
export type PurchaseOutcome = { order: CrystalOrder; dialog: 'pending' | 'cancelled' | 'failed' | 'unknown' | 'not-opened' }
export type RewardOutcome =
  | { status: 'unavailable' }
  | { status: 'closed'; session: RewardedSession }
  | { status: 'completed'; session: RewardedSession }
  | { status: 'pending'; session: RewardedSession; requestId: string }

/** UI-independent orchestration. The merchant mockup is intentionally not wired.
 * Call purchase/watchReward only from an explicit player action; show the server
 * price/reward beforehand. Recovery only reads, never opens another VK dialog. */
export class VkCommerce {
  private busy = false
  private readonly opened = new Set<string>()
  private readonly adResults = new Map<string, { rewarded: boolean; requestId: string }>()
  constructor(private readonly api: Transport, private readonly sdk: SDK, private readonly launch: () => string) {}

  async view(): Promise<MonetizationView> {
    const view = await this.api.get<MonetizationView>('/api/v1/monetization')
    if (view.platform !== 'vk' || view.app_id !== VK_APP_ID) throw new VkUnavailable()
    const capabilities = await this.sdk.capabilities({ payments: view.payments, rewarded: view.rewarded })
    return { ...view, payments: capabilities.payments, rewarded: capabilities.rewarded }
  }

  recoverOrders(): Promise<CrystalOrders> {
    return this.api.get<CrystalOrders>('/api/v1/monetization/orders')
  }

  readOrder(id: string): Promise<CrystalOrder> {
    return this.api.get<CrystalOrder>(`/api/v1/monetization/orders/${encodeURIComponent(id)}`)
  }

  private async exclusive<T>(run: () => Promise<T>): Promise<T> {
    if (this.busy) throw new VkBusy()
    this.busy = true
    try { return await run() } finally { this.busy = false }
  }

  /** Keep the same requestId when the create-order response is lost. A second
   * call with it reads the order and never reopens a dialog already attempted. */
  purchase(sku: string, requestId: string, testOnly = false, recoveryOnly = false): Promise<PurchaseOutcome> {
    return this.exclusive(async () => {
      const view = await this.view()
      if (testOnly && !view.test_mode) throw new VkUnavailable()
      if (!view.payments || !view.products.some(p => p.sku === sku)) throw new VkUnavailable()
      const created = await this.api.command<CrystalOrder>('/api/v1/monetization/orders', {
        request_id: requestId, sku, launch_params: this.launch(), ...(testOnly ? { test_only: true } : {}),
      })
      // The command journal can replay an old pending response after delivery.
      const order = await this.readOrder(created.id)
      if (testOnly && !order.test_mode) throw new VkUnavailable()
      if (recoveryOnly || order.status !== 'pending' || this.opened.has(order.id)) return { order, dialog: 'not-opened' }
      this.opened.add(order.id)
      const dialog = await this.sdk.purchase(order.item, true)
      // No client verification/credit endpoint. VK's signed callback grants.
      return { order: await this.readOrder(order.id), dialog: dialog.status }
    })
  }

  watchReward(requestId: string, pause: () => () => void): Promise<RewardOutcome> {
    return this.exclusive(async () => {
      const view = await this.view()
      if (!view.rewarded || view.ad_remaining < 1 || !await this.sdk.rewardAvailable(true)) return { status: 'unavailable' }
      const reserved = await this.api.command<RewardedSession>('/api/v1/monetization/ads/start', {
        request_id: requestId, placement: 'city_crystals', launch_params: this.launch(),
      })
      const session = await this.api.get<RewardedSession>(`/api/v1/monetization/ads/${encodeURIComponent(reserved.id)}`)
      if (session.status !== 'reserved') return { status: session.status === 'completed' ? 'completed' : 'closed', session }
      let result = this.adResults.get(session.id)
      if (!result) {
        let rewarded = false
        try { rewarded = await this.sdk.showReward(true, pause) === 'reward' } catch { /* release reservation below */ }
        result = { rewarded, requestId: crypto.randomUUID() }
        this.adResults.set(session.id, result)
      }
      if (!result.rewarded) {
        const cancelled = await this.api.command<RewardedSession>(`/api/v1/monetization/ads/${encodeURIComponent(session.id)}/cancel`, { request_id: result.requestId, rewarded: false })
        return { status: 'closed', session: cancelled }
      }
      return this.completeReward(session, result.requestId)
    })
  }

  /** Retry only an operation whose SDK reward callback already happened. Keep
   * this result in UI state on network loss; don't show an ad or cancel again. */
  async completeReward(session: RewardedSession, requestId: string): Promise<RewardOutcome> {
    try {
      const completed = await this.api.command<RewardedSession>(`/api/v1/monetization/ads/${encodeURIComponent(session.id)}/complete`, { request_id: requestId, rewarded: true })
      return { status: 'completed', session: completed }
    } catch (error) {
      if (error && typeof error === 'object' && 'code' in error && error.code === 'network') return { status: 'pending', session, requestId }
      throw error
    }
  }
}

let commerce: Promise<VkCommerce> | null = null
export function vkCommerce(): Promise<VkCommerce> {
  if (!commerce) commerce = vkSdk().then(sdk => new VkCommerce(http, sdk, vkLaunchParams)).catch(e => { commerce = null; throw e })
  return commerce
}
